import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Download, MapPin, MapPinned, MapPinOff, RefreshCw, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useRoleModules } from "../lib/roleModules";
import { StaffLabel } from "./StaffAvatar";
import { AssignWardPopover } from "./AssignWardPopover";
import { MohallaBackfillDialog } from "./MohallaBackfillDialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { TableRowsSkeleton } from "./PaginationBar";
import { api, downloadBlob, errMsg, fmtDateTime } from "../lib/api";

/**
 * Phase 92 — Unassigned Wards Report.
 * Wards whose properties ended up with NO covering collector after an auto-assign run (territory gaps).
 * Two sources: the snapshot stored on the last finished run, or a live recompute of the register.
 * Every row carries the ward's *current* coverage, so a territory fix flips it to "covered" at once.
 */

export const COVERAGE_META = {
  uncovered: { label: "No collector covers this ward", short: "Uncovered", cls: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertTriangle },
  partial: { label: "Only some mohallas of this ward are covered", short: "Partly covered", cls: "bg-orange-50 text-orange-800 border-orange-200", icon: MapPin },
  covered: { label: "Covered now — run auto-assign to apply", short: "Covered now", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  no_ward: { label: "Properties have no ward recorded", short: "No ward", cls: "bg-slate-100 text-slate-700 border-slate-200", icon: MapPinOff },
};

/** Client-side mirror of the backend summary so KPI chips update instantly after a quick assign. */
export const summarizeRows = (rows) => {
  const real = rows.filter((r) => !r.no_ward);
  return {
    wards: real.length,
    properties: rows.reduce((s, r) => s + (r.count || 0), 0),
    uncovered: real.filter((r) => r.coverage === "uncovered").length,
    partial: real.filter((r) => r.coverage === "partial").length,
    covered_now: real.filter((r) => r.coverage === "covered").length,
    no_ward_properties: rows.filter((r) => r.no_ward).reduce((s, r) => s + (r.count || 0), 0),
  };
};

export function useUnassignedWards(code, { initialSource = "last_run", auto = true, initialZoneId = "" } = {}) {
  const [report, setReport] = useState(null);
  const [source, setSource] = useState(initialSource);
  const [zoneId, setZoneIdState] = useState(initialZoneId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const seq = useRef(0);
  const sourceRef = useRef(initialSource);   // current source without re-creating `load`
  const zoneRef = useRef(initialZoneId);     // Phase 137: current zone filter (server-side)

  const load = useCallback(async (src, { silent = false, zone } = {}) => {
    src = src || sourceRef.current;
    const z = zone === undefined ? zoneRef.current : zone;
    const my = ++seq.current;
    sourceRef.current = src;
    zoneRef.current = z;
    setSource(src);
    setZoneIdState(z);
    setLoading(true);
    if (!silent) setError(null);
    try {
      const r = await api.get(`/t/${code}/assignments/unassigned-wards`, { params: { source: src, ...(z ? { zone_id: z } : {}) } });
      if (my !== seq.current) return null; // a newer request superseded this one
      setReport(r.data);
      setError(null);
      return r.data;
    } catch (e) {
      if (my === seq.current) setError(e);
      return null;
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, [code]);

  // initial fetch (once per tenant); source switches happen through load()/refreshLive()/showLastRun()
  useEffect(() => { if (auto) load(initialSource); }, [code, auto, initialSource, load]);

  const exportXlsx = useCallback(async () => {
    setExporting(true);
    try {
      const src = sourceRef.current;
      const z = zoneRef.current;
      const stamp = new Date().toISOString().slice(0, 10);
      await downloadBlob(`/t/${code}/assignments/unassigned-wards/export?source=${src}${z ? `&zone_id=${encodeURIComponent(z)}` : ""}`,
        `unassigned-wards_${code}_${src}_${stamp}.xlsx`);
      toast.success("Unassigned wards report exported");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  }, [code]);

  /** Phase 96: after a quick assign, patch the row locally (drop it, or lower its count) — no refetch. */
  const applyAssignResult = useCallback((res) => {
    if (!res?.ward?.id) return;
    setReport((rep) => {
      if (!rep) return rep;
      const rows = (rep.rows || []).flatMap((r) => (r.ward_id === res.ward.id ? (res.row ? [res.row] : []) : [r]));
      return { ...rep, rows, summary: summarizeRows(rows) };
    });
  }, []);

  return {
    report, source, zoneId, loading, error, exporting, exportXlsx,
    reload: () => load(sourceRef.current),
    refreshLive: () => load("live"),
    showLastRun: () => load("last_run"),
    setZone: (z) => load(sourceRef.current, { zone: z || "" }),   // Phase 137: re-fetch with the zone filter
    load,
    applyAssignResult,
  };
}


export const CoverageBadge = ({ coverage, testId }) => {
  const meta = COVERAGE_META[coverage] || COVERAGE_META.uncovered;
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={`text-[11px] font-medium gap-1 whitespace-nowrap ${meta.cls}`} data-testid={testId} data-coverage={coverage}>
      <Icon className="h-3 w-3" /> {meta.label}
    </Badge>
  );
};

export const SourceBadge = ({ report, testId = "unassigned-wards-source-badge" }) => {
  if (!report) return null;
  const live = report.source === "live";
  return (
    <Badge variant="outline" className={`text-[11px] gap-1 ${live ? "bg-sky-100 text-sky-800 border-sky-200" : "bg-muted text-muted-foreground border-border"}`} data-testid={testId} data-source={report.source}>
      <RefreshCw className="h-3 w-3" />
      {live ? `Live • as of ${fmtDateTime(report.as_of)}` : `Last run • finished ${fmtDateTime(report.as_of)}`}
    </Badge>
  );
};

const rowKey = (r) => (r.no_ward ? "no-ward" : r.ward_id);

export function UnassignedWardsTable({ rows, code, dense = false, onAssigned, onBackfilled }) {
  const { auth } = useAuth();
  const access = useRoleModules(code);
  const isAdmin = auth?.user?.role === "municipal_admin";
  // Phase 156: "partly covered" wards have mohalla-level collectors only — properties without a mohalla are stranded.
  // Filling their mohallas (Mohalla Backfill) hands them to those collectors; the sync runs automatically.
  const canBackfill = isAdmin && access.canWrite("masters", "mohallas");
  const [backfillWard, setBackfillWard] = useState(null);
  return (
    <div className="overflow-x-auto">
      {canBackfill && (
        <MohallaBackfillDialog code={code} open={!!backfillWard} onOpenChange={(v) => { if (!v) setBackfillWard(null); }}
          wardId={backfillWard} onDone={() => onBackfilled?.()} />
      )}
      <Table data-testid="unassigned-wards-table">
        <TableHeader>
          <TableRow>
            <TableHead>Zone</TableHead>
            <TableHead>Ward</TableHead>
            <TableHead className="text-right whitespace-nowrap">Unassigned</TableHead>
            <TableHead>Coverage</TableHead>
            <TableHead className="min-w-[220px]">Sample PINs</TableHead>
            {isAdmin && <TableHead className="text-right">Fix</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const k = rowKey(r);
            return (
              <TableRow key={k} className="hover:bg-muted/60" data-testid={`unassigned-ward-row-${k}`} data-coverage={r.coverage}>
                <TableCell className={`${dense ? "py-2" : "py-3"} text-sm text-muted-foreground whitespace-nowrap`}>{r.zone_name || "—"}</TableCell>
                <TableCell className={`${dense ? "py-2" : "py-3"} text-sm font-medium whitespace-nowrap`} data-testid={`unassigned-ward-name-${k}`}>
                  {r.ward_name}{r.ward_code ? <span className="ml-1 text-xs text-muted-foreground font-mono">({r.ward_code})</span> : null}
                </TableCell>
                <TableCell className={`${dense ? "py-2" : "py-3"} text-sm text-right font-semibold tabular-nums`} data-testid={`unassigned-ward-count-${k}`}>
                  {(r.count ?? 0).toLocaleString("en-IN")}
                </TableCell>
                <TableCell className={`${dense ? "py-2" : "py-3"}`}>
                  <div className="flex flex-col gap-1 items-start">
                    <CoverageBadge coverage={r.coverage} testId={`unassigned-ward-coverage-${k}`} />
                    {r.covered_by?.length > 0 && (
                      <span className="text-[11px] text-muted-foreground" data-testid={`unassigned-ward-collectors-${k}`}>
                        {r.covered_by.slice(0, 3).map((c, i) => <React.Fragment key={c.id}>{i > 0 ? ", " : ""}<StaffLabel code={code} userId={c.id} name={c.name} />{c.whole_ward ? "" : " (some mohallas)"}</React.Fragment>)}
                        {r.covered_by.length > 3 ? ` +${r.covered_by.length - 3} more` : ""}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className={`${dense ? "py-2" : "py-3"}`}>
                  <div className="flex flex-wrap gap-1" data-testid={`unassigned-ward-pins-${k}`}>
                    {(r.sample_pins || []).map((p) => (
                      <Link key={p} to={`/${code}/${isAdmin ? "admin" : "supervisor"}/properties?search=${encodeURIComponent(p)}`}
                        className="font-mono text-[11px] rounded border bg-muted/40 px-1.5 py-0.5 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid={`unassigned-ward-pin-${p}`}>{p}</Link>
                    ))}
                    {r.count > (r.sample_pins || []).length && (
                      <span className="text-[11px] text-muted-foreground self-center">+{(r.count - (r.sample_pins || []).length).toLocaleString("en-IN")} more</span>
                    )}
                  </div>
                </TableCell>
                {isAdmin && (
                  <TableCell className={`${dense ? "py-2" : "py-3"} text-right`}>
                    {r.no_ward ? (
                      <span className="text-[11px] text-muted-foreground whitespace-nowrap" data-testid={`unassigned-ward-fix-${k}`}>Set the ward on each PIN</span>
                    ) : (
                      <div className="inline-flex items-center justify-end gap-1 whitespace-nowrap" data-testid={`unassigned-ward-actions-${k}`}>
                        <AssignWardPopover code={code} row={r} onAssigned={onAssigned} dense={dense} />
                        {canBackfill && r.coverage === "partial" && (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button size="sm" variant="outline" className={`${dense ? "h-7" : "h-8"} border-orange-200 text-orange-800 hover:bg-orange-50 hover:text-orange-900`}
                                  onClick={() => setBackfillWard(r.ward_id)} data-testid={`unassigned-ward-backfill-${k}`}>
                                  <MapPinned className="h-3.5 w-3.5 mr-1" /> Fill mohallas
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="max-w-xs">
                                Collectors here are mapped by mohalla, so properties without a mohalla reach nobody. Fill their mohallas and they are assigned automatically.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        <Button asChild size="sm" variant="ghost" className={dense ? "h-7" : "h-8"} data-testid={`unassigned-ward-fix-${k}`}>
                          <Link to={`/${code}/admin/users${r.zone_id ? `?zone_id=${r.zone_id}` : ""}`}><Users className="h-3.5 w-3.5 mr-1" /> Territories</Link>
                        </Button>
                      </div>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export const UnassignedWardsEmpty = ({ report }) => (
  <div className="p-8 text-center" data-testid="unassigned-wards-empty">
    <div className="h-11 w-11 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-3">
      <CheckCircle2 className="h-5 w-5" />
    </div>
    <p className="text-sm font-medium">{report?.zone ? `No territory gaps in ${report.zone.zone_name}` : "No territory gaps"}</p>
    <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
      {report?.zone
        ? (report.all_summary?.wards
          ? `Every ward of this zone is covered. ${report.all_summary.wards} ward${report.all_summary.wards === 1 ? "" : "s"} with gaps exist in other zones — switch to All zones to see them.`
          : "Every ward of this zone is covered.")
        : report?.source === "live"
          ? "Every active property is either assigned or sits in a ward with a covering collector."
          : "The last auto-assign run found a covering collector for every property it scanned."}
    </p>
  </div>
);

export const SummaryChips = ({ summary, testIdPrefix = "unassigned-wards-summary" }) => {
  if (!summary) return null;
  const chip = (label, value, cls, id) => (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs tabular-nums ${cls}`} data-testid={`${testIdPrefix}-${id}`}>
      <span className="font-semibold">{Number(value || 0).toLocaleString("en-IN")}</span> {label}
    </span>
  );
  return (
    <div className="flex flex-wrap gap-1.5">
      {chip(summary.wards === 1 ? "ward with gaps" : "wards with gaps", summary.wards, summary.wards ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-muted text-muted-foreground border-border", "wards")}
      {chip("unassigned properties", summary.properties, "bg-muted text-foreground border-border", "properties")}
      {summary.covered_now > 0 && chip("covered now", summary.covered_now, "bg-emerald-50 text-emerald-800 border-emerald-200", "covered")}
      {summary.partial > 0 && chip("partly covered", summary.partial, "bg-orange-50 text-orange-800 border-orange-200", "partial")}
      {summary.no_ward_properties > 0 && chip("without a ward", summary.no_ward_properties, "bg-slate-100 text-slate-700 border-slate-200", "no-ward")}
    </div>
  );
};

/** Notes that explain where the numbers come from (fallback / pre-Phase-92 run / stale while running). */
export const ReportNotes = ({ report, running }) => {
  if (!report) return null;
  const notes = [];
  if (running) notes.push({ id: "running", text: "An auto-assign run is in progress — the last-run snapshot refreshes automatically when it finishes." });
  if (report.fallback) notes.push({ id: "fallback", text: "No auto-assign run has finished yet, so this is a live view of the current register." });
  if (report.source === "last_run" && report.job?.snapshot_missing) notes.push({ id: "snapshot-missing", text: "The last run predates the ward report. Use Refresh live, or run auto-assign again to capture a snapshot." });
  if (!notes.length) return null;
  return (
    <div className="space-y-1">
      {notes.map((n) => <p key={n.id} className="text-xs text-muted-foreground" data-testid={`unassigned-wards-note-${n.id}`}>{n.text}</p>)}
    </div>
  );
};

/**
 * Card for the Assignment Workspace (sits right below the auto-assign progress tracker).
 * `refreshKey` — bump to reload the last-run snapshot (e.g. when a run finishes).
 */
export function UnassignedWardsPanel({ code, refreshKey = 0, running = false, defaultOpen = true }) {
  const uw = useUnassignedWards(code);
  const [open, setOpen] = useState(defaultOpen);
  const { auth } = useAuth();
  const isAdmin = auth?.user?.role === "municipal_admin";
  const firstKey = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey !== firstKey.current) { firstKey.current = refreshKey; uw.showLastRun(); }
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = uw.report?.rows || [];
  const gapWards = uw.report?.summary?.wards || 0;

  return (
    <Card className="shadow-sm mb-4" data-testid="unassigned-wards-panel" data-source={uw.report?.source || ""}>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <MapPinOff className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium">Unassigned wards</p>
              <p className="text-xs text-muted-foreground">Wards whose properties had no covering collector — use Assign to map a collector from that zone in one step, or fix territories and run auto-assign again.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <SourceBadge report={uw.report} />
            <TooltipProvider delayDuration={200}>
              {uw.source === "live" ? (
                <Button size="sm" variant="outline" onClick={uw.showLastRun} disabled={uw.loading} data-testid="unassigned-wards-show-last-run">
                  Show last run
                </Button>
              ) : null}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="sm" variant="outline" onClick={uw.refreshLive} disabled={uw.loading} data-testid="unassigned-wards-refresh-live">
                    <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${uw.loading ? "animate-spin" : ""}`} /> Refresh live
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Recompute from the current register without starting a run</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button size="sm" variant="outline" onClick={uw.exportXlsx} disabled={uw.exporting || !rows.length} data-testid="unassigned-wards-export">
              <Download className="h-3.5 w-3.5 mr-1.5" /> {uw.exporting ? "Preparing…" : "Export Excel"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="unassigned-wards-toggle">
              {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              <span className="sr-only">{open ? "Collapse" : "Expand"}</span>
            </Button>
          </div>
        </div>

        {uw.report && <SummaryChips summary={uw.report.summary} />}
        <ReportNotes report={uw.report} running={running} />

        {open && (
          <div className="rounded-md border overflow-hidden">
            {uw.loading && !uw.report ? (
              <TableRowsSkeleton rows={4} cols={5} testId="unassigned-wards-skeleton" />
            ) : uw.error ? (
              <div className="p-6 text-center space-y-2" data-testid="unassigned-wards-error">
                <p className="text-sm text-destructive">{errMsg(uw.error)}</p>
                <Button size="sm" variant="outline" onClick={uw.reload}>Retry</Button>
              </div>
            ) : rows.length === 0 ? (
              <UnassignedWardsEmpty report={uw.report} />
            ) : (
              <UnassignedWardsTable rows={rows} code={code} dense onAssigned={uw.applyAssignResult} onBackfilled={uw.refreshLive} />
            )}
          </div>
        )}

        {open && gapWards > 0 && (
          <p className="text-[11px] text-muted-foreground" data-testid="unassigned-wards-hint">
            {isAdmin
              ? <>Use <b>Assign</b> on a row to pick a collector from that zone (territory + properties in one step), or map territories under <Link to={`/${code}/admin/users`} className="underline underline-offset-2 hover:text-foreground" data-testid="unassigned-wards-users-link">Users &amp; Staff</Link> and run auto-assign again.</>
              : "Ask your Municipal Admin to map a tax collector to these wards, then run auto-assign again."}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
